/**
 * Pure derivation + preset logic for the Agent Autonomy slider.
 *
 * The slider is a DERIVED view over the agent's stored grants — nothing new
 * is stored (docs/claude/AGENT_AUTONOMY.md § Settings UI). Two axes:
 *
 *   - level    = how much (verbs). Stops are nested by reversibility: each
 *                stop is a strict superset of the previous one.
 *   - coverage = where (scopes: contacts & companies, each case-kind pipeline).
 *
 * The level is the stop MOST granted scopes sit at (each scope's own stop is
 * the highest k it carries at least V_k for; ties resolve upward). A scope
 * may then sit above or below the level — "Customized" means at least one
 * does — and the tune panel names the difference per scope. A per-scope
 * exception therefore changes that scope's row, not the whole card: with a
 * minimum rule, unticking one verb on one pipeline dragged the level, the
 * stepper, and every other scope's status down with it.
 *
 * This module is Django-free, React-free, and fully unit-tested — keep it
 * that way. Copy lives in agent-autonomy-copy.ts, not here.
 */

import type { AgentCaseType, PipelineGrant } from './hooks/useAgentSettings';

export const AUTONOMY_STOPS = [0, 1, 2, 3] as const;
export type AutonomyStop = (typeof AUTONOMY_STOPS)[number];
export const MIN_AUTONOMY_STOP: AutonomyStop = 0;
export const MAX_AUTONOMY_STOP: AutonomyStop = 3;

/** The stop new agents are provisioned at, and the one a first enable from
 *  nothing granted applies (owner ruling 2026-09-12). */
export const DEFAULT_AUTONOMY_STOP: AutonomyStop = 2;

export type PipelineVerb = keyof PipelineGrant;
export type ContactsVerb = 'mergeContacts' | 'assignContacts' | 'editContacts';

const PIPELINE_VERBS: readonly PipelineVerb[] = [
  'markCompleted',
  'moveBackward',
  'mergeDuplicates',
  'assignToTeammates',
  'editEnteredDetails',
];

const CONTACTS_VERBS: readonly ContactsVerb[] = [
  'mergeContacts',
  'assignContacts',
  'editContacts',
];

/** V_k for a pipeline scope. Stop 0 and 1 grant no extras. */
const PIPELINE_VERBS_BY_STOP: Record<
  AutonomyStop,
  ReadonlySet<PipelineVerb>
> = {
  0: new Set(),
  1: new Set(),
  2: new Set(['markCompleted', 'moveBackward', 'assignToTeammates']),
  3: new Set(PIPELINE_VERBS),
};

/** V_k for the contacts & companies scope. */
const CONTACTS_VERBS_BY_STOP: Record<
  AutonomyStop,
  ReadonlySet<ContactsVerb>
> = {
  0: new Set(),
  1: new Set(),
  2: new Set(['assignContacts']),
  3: new Set(CONTACTS_VERBS),
};

/** The stop that first includes each verb — the order verbs are listed in,
 *  and the line between "part of the level" and "beyond it". */
export const PIPELINE_VERB_STOPS: Record<PipelineVerb, AutonomyStop> = {
  markCompleted: 2,
  moveBackward: 2,
  assignToTeammates: 2,
  mergeDuplicates: 3,
  editEnteredDetails: 3,
};

export const CONTACTS_VERB_STOPS: Record<ContactsVerb, AutonomyStop> = {
  assignContacts: 2,
  mergeContacts: 3,
  editContacts: 3,
};

/** The verbs a pipeline grant carries beyond V_level, in list order. */
export function pipelineVerbsAboveLevel(
  grant: PipelineGrant,
  level: AutonomyStop
): PipelineVerb[] {
  const included = PIPELINE_VERBS_BY_STOP[level];
  return PIPELINE_VERBS.filter(verb => grant[verb] && !included.has(verb));
}

/** The contacts verbs stored beyond V_level, in list order. */
export function contactsVerbsAboveLevel(
  state: AutonomyGrantState,
  level: AutonomyStop
): ContactsVerb[] {
  const included = CONTACTS_VERBS_BY_STOP[level];
  return CONTACTS_VERBS.filter(verb => state[verb] && !included.has(verb));
}

/** The verbs in V_level a pipeline grant is missing, in list order. */
export function pipelineVerbsBelowLevel(
  grant: PipelineGrant,
  level: AutonomyStop
): PipelineVerb[] {
  const included = PIPELINE_VERBS_BY_STOP[level];
  return PIPELINE_VERBS.filter(verb => included.has(verb) && !grant[verb]);
}

/** The contacts verbs in V_level that are off, in list order. */
export function contactsVerbsBelowLevel(
  state: AutonomyGrantState,
  level: AutonomyStop
): ContactsVerb[] {
  const included = CONTACTS_VERBS_BY_STOP[level];
  return CONTACTS_VERBS.filter(verb => included.has(verb) && !state[verb]);
}

export function pipelineVerbsForStop(
  stop: AutonomyStop
): ReadonlySet<PipelineVerb> {
  return PIPELINE_VERBS_BY_STOP[stop];
}

export function contactsVerbsForStop(
  stop: AutonomyStop
): ReadonlySet<ContactsVerb> {
  return CONTACTS_VERBS_BY_STOP[stop];
}

/** The stored grants, as read by useAgentSettings. */
export interface AutonomyGrantState {
  addContacts: boolean;
  mergeContacts: boolean;
  assignContacts: boolean;
  editContacts: boolean;
  /** Keyed by entity type name. A present key IS the base grant. */
  pipelineGrants: Record<string, PipelineGrant>;
}

export type AutonomyCoverage = 'full' | 'partial' | 'none';

export interface AutonomyDerivation {
  level: AutonomyStop;
  /** Some granted scope carries other than exactly V_level. */
  customized: boolean;
  contactsGranted: boolean;
  /** Case types (in schema order) that carry a grant row. */
  grantedPipelines: AgentCaseType[];
  /** Case types (in schema order) with no grant row. */
  missingPipelines: AgentCaseType[];
  coverage: AutonomyCoverage;
  /** Scopes whose grants differ from V_level, above or below. */
  scopesOffLevel: { contacts: boolean; pipelines: string[] };
}

function pipelineGrantFor(verbs: ReadonlySet<PipelineVerb>): PipelineGrant {
  return {
    markCompleted: verbs.has('markCompleted'),
    moveBackward: verbs.has('moveBackward'),
    mergeDuplicates: verbs.has('mergeDuplicates'),
    assignToTeammates: verbs.has('assignToTeammates'),
    editEnteredDetails: verbs.has('editEnteredDetails'),
  };
}

function pipelineCarriesAtLeast(
  grant: PipelineGrant,
  verbs: ReadonlySet<PipelineVerb>
): boolean {
  for (const verb of verbs) {
    if (!grant[verb]) {
      return false;
    }
  }
  return true;
}

function pipelineCarriesExactly(
  grant: PipelineGrant,
  verbs: ReadonlySet<PipelineVerb>
): boolean {
  return PIPELINE_VERBS.every(verb => grant[verb] === verbs.has(verb));
}

function contactsCarriesAtLeast(
  state: AutonomyGrantState,
  verbs: ReadonlySet<ContactsVerb>
): boolean {
  for (const verb of verbs) {
    if (!state[verb]) {
      return false;
    }
  }
  return true;
}

function contactsCarriesExactly(
  state: AutonomyGrantState,
  verbs: ReadonlySet<ContactsVerb>
): boolean {
  return CONTACTS_VERBS.every(verb => state[verb] === verbs.has(verb));
}

/** The highest stop a granted pipeline carries at least V_k for. */
function pipelineOwnStop(grant: PipelineGrant): AutonomyStop {
  for (const stop of [3, 2] as const) {
    if (pipelineCarriesAtLeast(grant, PIPELINE_VERBS_BY_STOP[stop])) {
      return stop;
    }
  }
  return 1;
}

/** The highest stop the granted contacts scope carries at least V_k for. */
function contactsOwnStop(state: AutonomyGrantState): AutonomyStop {
  for (const stop of [3, 2] as const) {
    if (contactsCarriesAtLeast(state, CONTACTS_VERBS_BY_STOP[stop])) {
      return stop;
    }
  }
  return 1;
}

/** The stop most scopes sit at; a tie goes to the higher stop, so unticking
 *  one verb on one scope never drags the level down. */
function modeStop(ownStops: readonly AutonomyStop[]): AutonomyStop {
  let best: AutonomyStop = 1;
  let bestCount = -1;
  for (const stop of [1, 2, 3] as const) {
    const count = ownStops.filter(s => s === stop).length;
    if (count >= bestCount && count > 0) {
      best = stop;
      bestCount = count;
    }
  }
  return best;
}

/**
 * Derive level, customization, and coverage from the stored grants.
 * Grant rows for types that are no longer case-kind are ignored (the
 * evaluator ignores them too); they are dropped on the next full rewrite.
 */
export function deriveAutonomy(
  state: AutonomyGrantState,
  caseTypes: readonly AgentCaseType[]
): AutonomyDerivation {
  const grantedPipelines = caseTypes.filter(
    t => t.entityTypeName in state.pipelineGrants
  );
  const missingPipelines = caseTypes.filter(
    t => !(t.entityTypeName in state.pipelineGrants)
  );
  const contactsGranted = state.addContacts;

  if (!contactsGranted && grantedPipelines.length === 0) {
    return {
      level: 0,
      customized: false,
      contactsGranted: false,
      grantedPipelines,
      missingPipelines,
      coverage: 'none',
      scopesOffLevel: { contacts: false, pipelines: [] },
    };
  }

  const ownStops: AutonomyStop[] = [
    ...(contactsGranted ? [contactsOwnStop(state)] : []),
    ...grantedPipelines.map(t =>
      pipelineOwnStop(state.pipelineGrants[t.entityTypeName])
    ),
  ];
  const level = modeStop(ownStops);

  const pipelinesOff = grantedPipelines
    .filter(
      t =>
        !pipelineCarriesExactly(
          state.pipelineGrants[t.entityTypeName],
          PIPELINE_VERBS_BY_STOP[level]
        )
    )
    .map(t => t.entityTypeName);
  const contactsOff =
    contactsGranted &&
    !contactsCarriesExactly(state, CONTACTS_VERBS_BY_STOP[level]);

  return {
    level,
    customized: contactsOff || pipelinesOff.length > 0,
    contactsGranted,
    grantedPipelines,
    missingPipelines,
    coverage:
      contactsGranted && missingPipelines.length === 0 ? 'full' : 'partial',
    scopesOffLevel: { contacts: contactsOff, pipelines: pipelinesOff },
  };
}

function withContactsVerbs(
  state: AutonomyGrantState,
  verbs: ReadonlySet<ContactsVerb>
): AutonomyGrantState {
  return {
    ...state,
    mergeContacts: verbs.has('mergeContacts'),
    assignContacts: verbs.has('assignContacts'),
    editContacts: verbs.has('editContacts'),
  };
}

/**
 * The grants after moving the slider to `stop`.
 *
 * - stop 0 removes every scope (contacts extras are left stored; the backend
 *   gates them off while `ahoy_add_contacts` is false).
 * - stop ≥ 1 with nothing granted grants EVERY scope at V_stop — the only way
 *   to leave Basics.
 * - stop ≥ 1 otherwise sets every GRANTED scope to exactly V_stop; coverage is
 *   untouched.
 */
export function applyLevel(
  state: AutonomyGrantState,
  caseTypes: readonly AgentCaseType[],
  stop: AutonomyStop
): AutonomyGrantState {
  if (stop === 0) {
    return { ...state, addContacts: false, pipelineGrants: {} };
  }
  const derived = deriveAutonomy(state, caseTypes);
  const pipelineVerbs = PIPELINE_VERBS_BY_STOP[stop];
  const contactsVerbs = CONTACTS_VERBS_BY_STOP[stop];

  if (derived.coverage === 'none') {
    const pipelineGrants: Record<string, PipelineGrant> = {};
    for (const t of caseTypes) {
      pipelineGrants[t.entityTypeName] = pipelineGrantFor(pipelineVerbs);
    }
    return {
      ...withContactsVerbs(state, contactsVerbs),
      addContacts: true,
      pipelineGrants,
    };
  }

  const pipelineGrants: Record<string, PipelineGrant> = {};
  for (const t of derived.grantedPipelines) {
    pipelineGrants[t.entityTypeName] = pipelineGrantFor(pipelineVerbs);
  }
  return {
    ...(derived.contactsGranted
      ? withContactsVerbs(state, contactsVerbs)
      : state),
    pipelineGrants,
  };
}

/** The full stop-`stop` matrix over every scope — used by a first enable
 *  from nothing granted (owner ruling: defaults on). */
export function matrixForStop(
  caseTypes: readonly AgentCaseType[],
  stop: AutonomyStop
): AutonomyGrantState {
  return applyLevel(
    {
      addContacts: false,
      mergeContacts: false,
      assignContacts: false,
      editContacts: false,
      pipelineGrants: {},
    },
    caseTypes,
    stop
  );
}

/** Grant one pipeline scope at the current level (or remove it). */
export function setPipelineScope(
  state: AutonomyGrantState,
  entityTypeName: string,
  granted: boolean,
  level: AutonomyStop
): AutonomyGrantState {
  if (granted) {
    return {
      ...state,
      pipelineGrants: {
        ...state.pipelineGrants,
        [entityTypeName]: pipelineGrantFor(
          PIPELINE_VERBS_BY_STOP[Math.max(level, 1) as AutonomyStop]
        ),
      },
    };
  }
  const pipelineGrants: Record<string, PipelineGrant> = {};
  for (const [name, grant] of Object.entries(state.pipelineGrants)) {
    if (name !== entityTypeName) {
      pipelineGrants[name] = grant;
    }
  }
  return { ...state, pipelineGrants };
}

/** Grant the contacts & companies scope at the current level (or remove it).
 *  Removing leaves the extras stored — the backend gates them. */
export function setContactsScope(
  state: AutonomyGrantState,
  granted: boolean,
  level: AutonomyStop
): AutonomyGrantState {
  if (!granted) {
    return { ...state, addContacts: false };
  }
  return {
    ...withContactsVerbs(
      state,
      CONTACTS_VERBS_BY_STOP[Math.max(level, 1) as AutonomyStop]
    ),
    addContacts: true,
  };
}

/** The wire shape of one `ahoy_pipeline_grants` row (strings only — the
 *  backend rejects booleans). A type alias, not an interface, so it stays
 *  assignable to the `Record<string, string>` row shape the event builder
 *  accepts. */
export type PipelineGrantWireRow = Record<
  | 'entity_type_name'
  | 'mark_completed'
  | 'move_backward'
  | 'merge_duplicates'
  | 'assign_to_teammates'
  | 'edit_entered_details',
  string
>;

/** The full-matrix payload for one `propertiesUpdated` event. */
export type AutonomyWireProps = {
  ahoy_add_contacts: string;
  ahoy_merge_contacts: string;
  ahoy_assign_contacts: string;
  ahoy_edit_contacts: string;
  ahoy_pipeline_grants: PipelineGrantWireRow[];
};

function bit(value: boolean): 'true' | 'false' {
  return value ? 'true' : 'false';
}

/**
 * Serialize the full matrix for one `propertiesUpdated` event. Rows follow
 * schema order for deterministic payloads; rows for unknown types are
 * dropped (stale grants for types that stopped being case-kind).
 */
export function toWireProps(
  state: AutonomyGrantState,
  caseTypes: readonly AgentCaseType[]
): AutonomyWireProps {
  const rows: PipelineGrantWireRow[] = [];
  for (const t of caseTypes) {
    const grant = state.pipelineGrants[t.entityTypeName];
    if (!grant) {
      continue;
    }
    rows.push({
      entity_type_name: t.entityTypeName,
      mark_completed: bit(grant.markCompleted),
      move_backward: bit(grant.moveBackward),
      merge_duplicates: bit(grant.mergeDuplicates),
      assign_to_teammates: bit(grant.assignToTeammates),
      edit_entered_details: bit(grant.editEnteredDetails),
    });
  }
  return {
    ahoy_add_contacts: bit(state.addContacts),
    ahoy_merge_contacts: bit(state.mergeContacts),
    ahoy_assign_contacts: bit(state.assignContacts),
    ahoy_edit_contacts: bit(state.editContacts),
    ahoy_pipeline_grants: rows,
  };
}

export function isAutonomyStop(value: number): value is AutonomyStop {
  return (AUTONOMY_STOPS as readonly number[]).includes(value);
}

// ---------------------------------------------------------------------------
// Capabilities — the rows of the ladder, and their real coverage.
// ---------------------------------------------------------------------------

/** Every thing the agent can do, keyed for copy lookup. Order is unlock order. */
export const CAPABILITY_KEYS = [
  'notes',
  'link',
  'fill',
  'contacts',
  'create',
  'terminal',
  'backward',
  'assign',
  'merge',
  'edit',
] as const;
export type CapabilityKey = (typeof CAPABILITY_KEYS)[number];

/** The stop that unlocks each capability. */
export const CAPABILITY_STOPS: Record<CapabilityKey, AutonomyStop> = {
  notes: 0,
  link: 0,
  fill: 0,
  contacts: 1,
  create: 1,
  terminal: 2,
  backward: 2,
  assign: 2,
  merge: 3,
  edit: 3,
};

function withPipelineVerb(
  state: AutonomyGrantState,
  verb: PipelineVerb,
  on: boolean
): AutonomyGrantState {
  // No pipeline scope is granted, so there is no verb to flip. Return the same
  // reference so the caller (setCapability's `next === grants` guard) treats
  // the press as a no-op instead of writing an event that changes nothing.
  if (Object.keys(state.pipelineGrants).length === 0) {
    return state;
  }
  const pipelineGrants: Record<string, PipelineGrant> = {};
  for (const [name, grant] of Object.entries(state.pipelineGrants)) {
    pipelineGrants[name] = { ...grant, [verb]: on };
  }
  return { ...state, pipelineGrants };
}

function withSharedVerb(
  state: AutonomyGrantState,
  contactsVerb: ContactsVerb,
  pipelineVerb: PipelineVerb,
  on: boolean
): AutonomyGrantState {
  const next = withPipelineVerb(state, pipelineVerb, on);
  // An ungranted contacts scope keeps its stored extras, as applyLevel does;
  // the backend gates them while `ahoy_add_contacts` is false.
  return state.addContacts ? { ...next, [contactsVerb]: on } : next;
}

/**
 * The grants after pressing one capability's mark on the ladder: the
 * capability turns on (or off) across EVERY scope it applies to, and the
 * scopes themselves are untouched — except for the two base capabilities,
 * which ARE scopes:
 *
 * - `contacts` grants or removes the contacts & companies scope at the
 *   current level.
 * - `create` grants every missing pipeline at the current level, or removes
 *   every pipeline grant.
 *
 * The floor capabilities never change.
 */
export function applyCapability(
  state: AutonomyGrantState,
  caseTypes: readonly AgentCaseType[],
  key: CapabilityKey,
  on: boolean
): AutonomyGrantState {
  switch (key) {
    case 'notes':
    case 'link':
    case 'fill':
      return state;
    case 'contacts':
      return setContactsScope(
        state,
        on,
        deriveAutonomy(state, caseTypes).level
      );
    case 'create': {
      if (!on) {
        return { ...state, pipelineGrants: {} };
      }
      const { level } = deriveAutonomy(state, caseTypes);
      let next = state;
      for (const t of caseTypes) {
        if (!(t.entityTypeName in next.pipelineGrants)) {
          next = setPipelineScope(next, t.entityTypeName, true, level);
        }
      }
      return next;
    }
    case 'terminal':
      return withPipelineVerb(state, 'markCompleted', on);
    case 'backward':
      return withPipelineVerb(state, 'moveBackward', on);
    case 'assign':
      return withSharedVerb(state, 'assignContacts', 'assignToTeammates', on);
    case 'merge':
      return withSharedVerb(state, 'mergeContacts', 'mergeDuplicates', on);
    case 'edit':
      return withSharedVerb(state, 'editContacts', 'editEnteredDetails', on);
    default:
      return assertNeverKey(key);
  }
}

function assertNeverKey(key: never): never {
  throw new Error(`Unhandled capability: ${String(key)}`);
}

/**
 * How much of the agent's coverage a capability actually holds — the truth,
 * including per-scope customizations the level hides:
 *
 *   all   — every applicable scope grants it
 *   some  — at least one but not every applicable scope grants it
 *   none  — no applicable scope grants it
 *   n/a   — nothing to apply it to (no case types in the org)
 */
export type CapabilityState = 'all' | 'some' | 'none' | 'n/a';

export interface CapabilityCoverage {
  state: CapabilityState;
  /** Labels of the scopes that grant it, in schema order (contacts first). */
  onLabels: string[];
  /** Number of scopes it could apply to. */
  total: number;
}

/**
 * `total` counts the scopes currently granted that the capability applies to;
 * `potential` counts the scopes that COULD be granted. A capability with no
 * granted scope is `none` ("nothing grants it yet") unless there is nothing it
 * could ever apply to (`n/a` — an org with no pipelines).
 */
function coverageOf(
  onLabels: string[],
  total: number,
  potential: number
): CapabilityCoverage {
  if (potential === 0) {
    return { state: 'n/a', onLabels, total };
  }
  if (onLabels.length === 0) {
    return { state: 'none', onLabels, total };
  }
  return {
    state: onLabels.length === total ? 'all' : 'some',
    onLabels,
    total,
  };
}

/**
 * Derive each capability's real coverage from the stored grants.
 * `contactsLabel` is the customer-facing label for the contacts scope, passed
 * in so this module stays copy-free.
 */
export function deriveCapabilityCoverage(
  state: AutonomyGrantState,
  caseTypes: readonly AgentCaseType[],
  contactsLabel: string
): Record<CapabilityKey, CapabilityCoverage> {
  const grantedPipelines = caseTypes.filter(
    t => t.entityTypeName in state.pipelineGrants
  );
  const pipelineLabelsWith = (verb: PipelineVerb) =>
    grantedPipelines
      .filter(t => state.pipelineGrants[t.entityTypeName][verb])
      .map(t => t.label);
  const sharedScopeTotal =
    (state.addContacts ? 1 : 0) + grantedPipelines.length;
  // Contacts & companies is always a potential scope; pipelines only if the
  // org has any.
  const sharedScopePotential = 1 + caseTypes.length;
  const sharedLabelsWith = (contactsVerb: ContactsVerb, verb: PipelineVerb) => [
    ...(state.addContacts && state[contactsVerb] ? [contactsLabel] : []),
    ...pipelineLabelsWith(verb),
  ];
  // The floor is always on while Active: nothing to scope, always granted.
  const floor: CapabilityCoverage = { state: 'all', onLabels: [], total: 1 };

  return {
    notes: floor,
    link: floor,
    fill: floor,
    contacts: coverageOf(state.addContacts ? [contactsLabel] : [], 1, 1),
    create: coverageOf(
      grantedPipelines.map(t => t.label),
      caseTypes.length,
      caseTypes.length
    ),
    terminal: coverageOf(
      pipelineLabelsWith('markCompleted'),
      grantedPipelines.length,
      caseTypes.length
    ),
    backward: coverageOf(
      pipelineLabelsWith('moveBackward'),
      grantedPipelines.length,
      caseTypes.length
    ),
    assign: coverageOf(
      sharedLabelsWith('assignContacts', 'assignToTeammates'),
      sharedScopeTotal,
      sharedScopePotential
    ),
    merge: coverageOf(
      sharedLabelsWith('mergeContacts', 'mergeDuplicates'),
      sharedScopeTotal,
      sharedScopePotential
    ),
    edit: coverageOf(
      sharedLabelsWith('editContacts', 'editEnteredDetails'),
      sharedScopeTotal,
      sharedScopePotential
    ),
  };
}
